import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const WavePickingForm = NiceModal.create(({ modalName, id }) => {
	const [loading, setLoading] = useState(false);
	const [sortingRequirement, setSortingRequirement] = useState(null);

	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();

	const axiosPrivate = useAxiosPrivate();

	const { closeModal } = useModalRouter();

	const schema = useMemo(
		() =>
			yup.object().shape({
				dropBinLocationID: yup.number().nullable().required(),
				waveItems: yup
					.array()
					.of(
						yup.object().shape({
							sku: yup.string().nullable().required(),
							quantity: yup.number().nullable().required(),
							binLocationID: yup
								.number()
								.nullable()
								.when('quantity', {
									is: (val) => val !== null && val !== undefined && val !== '' && val !== 0,
									then: (schema) => schema.required()
								})
						})
					)
					.nullable()
					.required()
			}),
		[]
	);

	const fillForm = async () => {
		try {
			setLoading(true);
			const response = await axiosPrivate.post(ADVANCE_WMS_MODULE.GET_WAVE_BY_ID, {
				id
			});
			const { data } = response;
			data.waveItems = data.waveItems
				.filter((item) => item.qtyTobePicked - item.qtyPicked !== 0)
				.map((item) => ({
					...item,
					quantity: item.qtyTobePicked - item.qtyPicked
				}));
			setSortingRequirement(data.sortingRequirement);
			reset(data);
		} catch (error) {
			AppLogger.error(error);
			showError();
			closeModal();
		} finally {
			setLoading(false);
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const { reset, watch, setValue, handleSubmit } = formMethods;

	const handleCloseModal = useCallback(() => closeModal(), [closeModal]);

	const onSubmit = (data) => {
		setLoading(true);

		axiosPrivate
			.post(ADVANCE_WMS_MODULE.COMPLETE_WAVE_PICKING, {
				waveId: id,
				toBinID: data.dropBinLocationID,
				items: data.waveItems
					.filter((item) => item.qtyTobePicked - item.qtyPicked !== 0)
					.map((item) => ({
						sku: item.sku,
						quantity: item.quantity,
						binId: item.binLocationID
					}))
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.WAVE_LIST));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				if (error.response?.data.code === 'error.OA001') {
					showError({
						message: 'error.thisCombinationAlreadyExists'
					});
				} else {
					AppLogger.error(error);
					showError();
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const binAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const columns = useMemo(
		() => [
			{
				id: 'sku',
				header: 'productName',
				size: 150,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.sku} - {row.original.productName}
					</Typography>
				)
			},
			{
				id: 'qtyTobePicked',
				header: 'qtyTobePicked',
				renderCell: ({ row }) => (
					<IntegerInput
						value={row.original.quantity}
						setValue={(val) => {
							if (row.original.qtyTobePicked - row.original.qtyPicked < val) return;
							setValue(`waveItems[${row.index}].quantity`, val, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						max={row.original.qtyTobePicked - row.original.qtyPicked}
						maxPlaceholder={row.original.qtyTobePicked - row.original.qtyPicked}
					/>
				)
			},
			{
				id: 'binLocationID',
				header: 'binLocation',
				size: 150,
				required: true,
				accessorKey: 'binLocationID',
				renderCell: ({ row }) => (
					<AutoComplete
						name={`waveItems[${row.index}].binLocationID`}
						autoCompleteName={AUTOCOMPLETE.PUT_AND_PICK_BINS}
						setValue={(value, firstRender) => {
							if (firstRender) return;
							setValue(`waveItems[${row.index}].binLocationID`, value?.ID, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						value={row.original.binLocationID}
						columns={binAutocompleteColumns}
						selectionLabelAccessor={['Name']}
						filterObject={{
							sku: row.original.sku
						}}
					/>
				)
			}
		],
		[]
	);

	const colSpan = {
		md: 6,
		lg: 6
	};

	return (
		<Modal name={modalName} maxWidth="large" fullwidth>
			<ModalTitle>
				<Typography variant="p1">wavePicking</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="waveNumber" isLoading={loading} colSpan={colSpan}>
						<StringInput name="waveNumber" disabled />
					</FormItem>
					<FormItem label="orderIds" isLoading={loading} colSpan={colSpan}>
						<StringInput name="orderIds" disabled />
					</FormItem>
					<FormItem label="startDate" isLoading={loading} colSpan={colSpan}>
						<DateInput name="startDate" value={watch('startDate')} disabled />
					</FormItem>
					<FormItem isLoading={loading} colSpan={colSpan} label="dropBinLocation" required>
						<AutoComplete
							name="dropBinLocationID"
							autoCompleteName={
								sortingRequirement ? AUTOCOMPLETE.SORTING_STATIONS : AUTOCOMPLETE.PACKING_BINS
							}
							setValue={(value, firstRender) => {
								if (firstRender) return;
								setValue('dropBinLocationID', value?.ID, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch('dropBinLocationID')}
							columns={binAutocompleteColumns}
							selectionLabelAccessor={['Name']}
						/>
					</FormItem>
					<Table data={watch('waveItems')} loading={loading} columns={columns} />
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					label="complete"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});
export default WavePickingForm;
